import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { buildFiscalProfileSummaryLines, formatFiscalProfileSummaryLine } from "@/lib/fiscalProfileSummary";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";

export interface FiscalSummarySectionProps {
  hideAgendaFields?: boolean;
}

export function FiscalSummarySection({
  hideAgendaFields = false,
}: FiscalSummarySectionProps): JSX.Element {
  const { t } = useTranslation();
  const { watch } = useFormContext<FiscalProfileEditorValues>();
  const values = watch();

  const lines = useMemo(
    () => buildFiscalProfileSummaryLines(values, { hideAgendaFields }).lines,
    [values, hideAgendaFields]
  );

  if (lines.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("settings.fiscalProfile.sections.summaryTitle")}
          </CardTitle>
          <CardDescription>
            {t("settings.fiscalProfile.sections.summaryDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("settings.fiscalProfile.summary.empty")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t("settings.fiscalProfile.sections.summaryTitle")}
        </CardTitle>
        <CardDescription>
          {t("settings.fiscalProfile.sections.summaryDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="list-inside list-disc space-y-1 text-sm text-foreground">
          {lines.map((line) => (
            <li key={line.id}>{formatFiscalProfileSummaryLine(line, t)}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
